import React from 'react';
import styled from 'styled-components';
/* eslint-disable import/no-extraneous-dependencies */
import SearchIcon from 'icons/search';

interface SearchContainerProps {
  height?: string;
  width?: string;
  currentSize?: string;
  collapsed?: boolean;
  primaryColor?: string;
  iconBgColor?: string;
}

interface InputProps {
  bgColor?: string;
  fontSize?: string;
  primaryColor?: string;
  iconBgColor?: string;
  fullyCollapsed?: boolean;
}

interface SearchIconContainerProps {
  primaryColor?: string;
  iconBgColor?: string;
  bgColor?: string;
  collapsed?: boolean;
}

const SearchContainer = styled.div<SearchContainerProps>`
  display: inline-block;
  position: relative;
  height: ${({ height }) => height};
  width: ${(props) => props.currentSize};
  overflow: hidden;
  border: 1px solid
    ${({ collapsed, primaryColor, iconBgColor }) => (collapsed ? iconBgColor : primaryColor)};
  border-radius: 6px;
  transition: width 0.3s;
`;

const Input = styled.input.attrs({
  type: 'text',
  placeholder: 'search',
})<InputProps>`
  height: 100%;
  width: 100%;
  padding: 0 35px 0 10px;
  outline: none;
  border: none;
  font-size: ${(props) => props.fontSize};
  background-color: ${({ bgColor, iconBgColor, fullyCollapsed }) =>
    fullyCollapsed ? iconBgColor : bgColor};
  &:focus {
    outline: none;
  }
`;

const SearchIconContainer = styled.div<SearchIconContainerProps>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 35px;
  padding-top: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ iconBgColor }) => iconBgColor};
  transition: all 0.3s;
`;

interface AutoCollapse {
  size: string;
}

interface FjSearchInputProps {
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  value?: string;
  primaryColor?: string;
  bgColor?: string;
  height?: string;
  width?: string;
  fontSize?: string;
  autoCollapse?: AutoCollapse;
  placeholder?: string;
  className?: string;
  iconBgColor?: string;
  iconSize?: string;
}

interface FjSearchInputState {
  collapsed: boolean;
  fullyCollapsed: boolean;
}

class FjSearchInput extends React.Component<FjSearchInputProps, FjSearchInputState> {
  static defaultProps = {
    iconBgColor: '#000000',
    primaryColor: '#FFFFFF',
    bgColor: 'white',
    height: '30px',
    width: '220px',
    fontSize: '1em',
    placeholder: '',
    iconSize: '20px',
  };

  inputRef: HTMLInputElement | null;

  constructor(props: FjSearchInputProps) {
    super(props);
    this.state = {
      collapsed: Boolean(props.autoCollapse && !props.value),
      fullyCollapsed: Boolean(props.autoCollapse && !props.value),
    };
    this.inputRef = null;
  }

  onInputFocus = (): void => {
    this.setState({
      collapsed: false,
      fullyCollapsed: false,
    });
  };

  onInputBlur = (): void => {
    if (this.props.autoCollapse && !this.props.value) {
      this.setState({
        collapsed: true,
      });
    }
  };

  onIconClick = (): void => {
    this.inputRef?.focus();
  };

  handleTransitionEnd = (): void =>
    this.setState(({ collapsed }) => ({ fullyCollapsed: collapsed }));

  render(): React.ReactNode {
    const {
      onChange,
      value,
      primaryColor,
      bgColor,
      height,
      width,
      fontSize,
      autoCollapse,
      placeholder,
      className,
      iconBgColor,
      iconSize,
    } = this.props;
    const { collapsed, fullyCollapsed } = this.state;
    const currentSize = collapsed && autoCollapse ? autoCollapse.size : width;
    return (
      <SearchContainer
        {...{ height, width, className, currentSize, collapsed, primaryColor, iconBgColor }}
        onTransitionEnd={this.handleTransitionEnd}
      >
        <Input
          {...{
            onChange,
            value,
            placeholder,
            bgColor,
            fontSize,
            primaryColor,
            iconBgColor,
            fullyCollapsed,
          }}
          ref={(ref) => {
            this.inputRef = ref;
          }}
          onFocus={this.onInputFocus}
          onBlur={this.onInputBlur}
        />
        <SearchIconContainer
          {...{ primaryColor, iconBgColor, bgColor, collapsed }}
          onClick={this.onIconClick}
          onMouseDown={(e) => e.preventDefault()}
        >
          <SearchIcon size={iconSize} />
        </SearchIconContainer>
      </SearchContainer>
    );
  }
}

export default FjSearchInput;
